import {useMutation} from '@tanstack/react-query'
import {useFormik} from 'formik'
import {FC} from 'react'
import {useDispatch} from 'react-redux'
import {Link, useHistory} from 'react-router-dom'
import {FormLabel, FormText, GButton} from 'src/app/components/Libs'
import GIcon from 'src/app/components/Libs/Icon/GIcon'
import {useOnline} from 'src/app/hooks/online.hook'
import {useWebTitle} from 'src/app/hooks/title.hook'
import {forgotPassword} from 'src/app/services'
import NotifAuthRedux from 'src/app/store/Auth/NotifAuthRedux'
import {getErrors} from 'src/app/utils/api.utils'
import {toAbsoluteUrl} from 'src/app/utils/asset-helpers.utils'
import {emailRegExp} from 'src/app/utils/input.utils'
import * as Yup from 'yup'
import AuthScreens from '../../AuthScreens'

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Silakan masukkan format email yang valid')
    .required('Masukkan alamat email Anda')
    .matches(emailRegExp, 'Format email salah')
    .max(100, 'Maksimal karakter 100'),
})

const initialValues = {
  email: '',
}

const ForgotPassword: FC = () => {
  useWebTitle('Lupa Kata Sandi')
  const {isOnline, errorOffline} = useOnline()
  const history = useHistory()
  const dispatch = useDispatch()

  const successSubmitFn = () => {
    const message = 'Tautan telah dikirim ke email Anda yang terdaftar'

    dispatch(NotifAuthRedux.actions.setNotification(message))
    history.push('/auth/login-email')
  }

  const submitFn = useMutation({
    mutationFn: (payload: typeof initialValues) => {
      return forgotPassword(payload)
    },
    onSuccess: () => {
      successSubmitFn()
    },
    onError: (e: any) => {
      const status = e.response?.status

      if (!isOnline) {
        formik.setStatus(errorOffline)
        return
      }

      if (isOnline) {
        if (status >= 500 || status === 404) {
          formik.setStatus('Layanan Sementara Tidak Tersedia. Silakan coba lagi nanti.')
        } else if (getErrors(e).detailCode === 'INACTIVE_ACCOUNT') {
          formik.setStatus('Akun tidak ditemukan, harap hubungi Administrator')
        } else if (status === 400) {
          const message = e.response?.data?.message || 'Email tidak valid atau belum terdaftar.'
          formik.setStatus(message)
        } else {
          formik.setStatus('Terjadi kesalahan. Silakan coba lagi.')
        }
      }
    },
  })

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values) => {
      formik.setStatus('')

      if (isOnline) {
        submitFn.mutate(values)
      } else {
        formik.setStatus(errorOffline)
      }
    },
  })

  return (
    <div data-testid='forgot-password-page' className='w-full'>
      <img
        src={toAbsoluteUrl('/media/logo/logo_optera.svg')}
        alt='optera_logo'
        className='h-[62px] mb-6'
      />

      <div className='mb-6'>
        <div className='mb-2 font-semibold text-fs-5 text-neutral-900'>Atur ulang Kata Sandi</div>
        <div className='text-neutral-600 text-fs-7'>Silakan masukkan email Anda yang terdaftar</div>
      </div>

      <form className='w-full' onSubmit={formik.handleSubmit} noValidate>
        <div className='mb-6'>
          <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
            Email
          </FormLabel>
          <FormText
            {...formik.getFieldProps('email')}
            name='email'
            type='email'
            placeholder='Masukkan email'
            maxLength={50}
            error={formik.errors.email}
            touched={formik.touched.email}
          />
        </div>

        {formik.status && (
          <div className='flex items-center p-4 mb-6 border rounded-lg drop-shadow-lg border-danger-500 bg-danger-50'>
            <div className='mr-3 text-danger-600'>
              <GIcon icon='IconCheckCircle' className='w-6 h-6' />
            </div>
            <span className='font-semibold text-fs-9 text-neutral-600'>{formik.status}</span>
          </div>
        )}

        <div className='flex items-center justify-between gap-4'>
          <Link
            to={AuthScreens.LOGIN_EMAIL.PATH}
            className='bg-white border border-primary-500 text-primary-500 font-medium flex-1 py-[10px] px-[20px] rounded-lg hover:bg-primary-25 transition text-center'
          >
            Kembali ke Login
          </Link>

          <GButton
            type='submit'
            size='large'
            className='flex-1'
            loading={submitFn.isLoading}
            disabled={submitFn.isLoading || !formik.dirty}
          >
            Lanjut
          </GButton>
        </div>
      </form>
    </div>
  )
}

export default ForgotPassword
